"use client"

import Title from "@/components/common/Typography/Title"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useTour } from "@/hooks/tour/useTour"
import { tourActions } from "@/lib/redux/features/tour/reduce"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { getTourPrice } from "@/utils/tour/getTourPrice"
import { getTourValidDayjs } from "@/utils/tour/getTourValidDayjs"
import { faChevronDown, faChevronRight, faChevronUp } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Card, Col, Row } from "antd"
import { useState } from "react"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { refDrawerInputBooking } from "../FooterRequestBookingTour"
import { useTranslation } from "react-i18next"

const defaultShownCount = 5
const DISPLAY_PRICE_MARKUP = 1.05

const getSlotBadgeModifier = (slots: number) => {
  if (!slots) return "contact"
  if (slots <= 5) return "low"
  return "available"
}

const TourPlanAndPrice = () => {
  const { getFormatCurrency } = useTETranslation()
  const [shownCount, setShownCount] = useState(defaultShownCount)
  const { tour: { data: tour } } = useTour()
  const inputTour = useAppSelector(state => state.tour.inputTour)
  const isDesktop = useIsDesktop()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const schedules = tour.departureSchedule || []
  const totalSchedules = schedules.length
  const isShowingAll = shownCount >= totalSchedules
  const hiddenScheduleCount = Math.max(0, totalSchedules - shownCount)

  if (!totalSchedules) return null

  return (
    <Col id="tour-section-schedule" span={24}>
      <Card
        className="tour-detail__section tour-detail__schedule-card"
        style={isDesktop ? {} : { borderRadius: 0 }}
        styles={{
          body: {
            paddingInline: 0,
            paddingBlock: "1.2rem",
          },
        }}
      >
        <Row justify="space-between" align="middle" className="passenger__form--row">
          <Col>
            <Title level={4} className="tour-detail__section-title">
              {t("tour.departure_schedule")}
            </Title>
          </Col>
        </Row>
        <div className="tour-detail__schedule-list passenger__form--row">
          <div className="tour-detail__schedule-list-head" aria-hidden>
            <span>{t("tour.departure_date")}</span>
            <span>{t("flight.return_date")}</span>
            <span>{t("tour.status_slot")}</span>
            <span>{t("common.price")}</span>
            <span />
          </div>
          {schedules.slice(0, shownCount).map((record) => {
            const unitPrice = record?.prices?.[0]?.priceAdt ?? 0
            const listPrice = Number.isFinite(unitPrice)
              ? Math.round(unitPrice * DISPLAY_PRICE_MARKUP)
              : null
            const badgeModifier = getSlotBadgeModifier(record.remainingSlots)
            const statusLabel = record.remainingSlots
              ? t("tour.status_slot_value", { slots: record.remainingSlots })
              : t("common.contact")

            return (
              <button
                key={record.id}
                type="button"
                className="tour-detail__schedule-row"
                onClick={() => {
                  if (isDesktop) {
                    window.scrollTo({ left: 0, top: 0, behavior: "smooth" })
                  }
                  dispatch(tourActions.selectDepartureSchedule(record.id))
                  if (!isDesktop && refDrawerInputBooking.current) {
                    refDrawerInputBooking.current?.open()
                  }
                }}
              >
                <span className="tour-detail__schedule-date">
                  {getTourValidDayjs(record.startDate).format("dd, DD/MM/YYYY")}
                </span>
                <span className="tour-detail__schedule-date">
                  {getTourValidDayjs(record.endDate).format("dd, DD/MM/YYYY")}
                </span>
                <span>
                  <span
                    className={`tour-detail__slot-badge tour-detail__slot-badge--${badgeModifier}`}
                  >
                    {statusLabel}
                  </span>
                </span>
                <span className="tour-detail__schedule-price">
                  {Number.isFinite(unitPrice) ? (
                    <>
                      {listPrice !== null && listPrice > unitPrice && (
                        <span className="tour-item__price-old tour-detail__schedule-price-old">
                          {getFormatCurrency(listPrice)}
                        </span>
                      )}
                      <span className="tour-item__price-sale tour-detail__schedule-price-sale">
                        {t("tour.price_per_guest", {
                          price: getFormatCurrency(unitPrice),
                        })}
                      </span>
                    </>
                  ) : (
                    getFormatCurrency(
                      getTourPrice({
                        ...inputTour,
                        selectedDepartureSchedule: record?.id,
                      }).totalAmount
                    )
                  )}
                </span>
                <span className="tour-detail__schedule-chevron" aria-hidden>
                  <FontAwesomeIcon icon={faChevronRight} />
                </span>
              </button>
            )
          })}
        </div>
        {totalSchedules > defaultShownCount && (
          <Row className="passenger__form--row">
            <Col span={24}>
              <Button
                block
                type="link"
                className="tour-detail__schedule-expand"
                icon={<FontAwesomeIcon icon={isShowingAll ? faChevronUp : faChevronDown} />}
                onClick={() =>
                  setShownCount(isShowingAll ? defaultShownCount : totalSchedules)
                }
              >
                {isShowingAll
                  ? t("common.see_less")
                  : t("tour.see_more_departures", { count: hiddenScheduleCount })}
              </Button>
            </Col>
          </Row>
        )}
      </Card>
    </Col>
  )
}

export default TourPlanAndPrice
